import { Layout } from "@/components/Layout";
import { NoindexSeo } from "@/components/Seo";
import { Link } from "react-router-dom";
import "./legal.css";

const UPDATED = "12 juillet 2026";
const CGU_VERSION = "1.0";

export default function CguPage() {
  return (
    <Layout>
      <NoindexSeo
        title="Conditions générales d'utilisation"
        path="/cgu"
      />
      <div className="legal">
        <div className="legal-wrap">
          <h1 className="legal-h1">Conditions générales d'utilisation</h1>
          <p className="legal-updated">Version {CGU_VERSION} · Mise à jour le {UPDATED}</p>

          <p className="legal-intro">
            Les présentes conditions générales d'utilisation (« CGU ») régissent
            l'accès et l'utilisation du site <b>Mon Diag Énergétique</b> (ci-après
            « le Service »), édité par <b>Pierre Thurau</b> (ci-après « l'Éditeur »).
            En créant un compte ou en utilisant le Service, vous acceptez sans
            réserve les présentes CGU.
          </p>

          <div className="legal-callout">
            <b>Rappel essentiel :</b> Mon Diag Énergétique est un outil
            pédagogique d'estimation. Il ne produit pas de DPE réglementaire et
            n'a pas de valeur légale. Pour un DPE officiel opposable, faites
            appel à un diagnostiqueur certifié (annuaire public :{" "}
            <a
              href="https://diagnostiqueurs.din.developpement-durable.gouv.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-link"
            >
              diagnostiqueurs.din.developpement-durable.gouv.fr
            </a>
            ).
          </div>

          <h2 className="legal-h2">1. Objet du Service</h2>
          <p className="legal-p">
            Mon Diag Énergétique est une application web permettant d'estimer la
            performance énergétique d'un logement à partir de ses
            caractéristiques (surface, isolation, système de chauffage, etc.). Le
            calcul s'appuie sur la méthode réglementaire 3CL-DPE 2021 (arrêté du
            31 mars 2021). Pour en savoir plus, consultez notre{" "}
            <Link to="/methodologie" className="legal-link">page méthodologie</Link>.
          </p>
          <p className="legal-p">
            Le Service fournit également une estimation du retour sur
            investissement de gestes de rénovation et des conseils personnalisés.
            Ces résultats sont <b>indicatifs</b> et ne remplacent pas l'avis d'un
            professionnel qualifié.
          </p>

          <h2 className="legal-h2">2. Accès au Service</h2>
          <p className="legal-p">
            L'accès au Service est ouvert à toute personne disposant d'un compte
            valide. L'inscription requiert une adresse e-mail valide et un mot de
            passe. La vérification de l'e-mail est obligatoire avant d'accéder
            aux fonctionnalités de calcul.
          </p>
          <p className="legal-p">
            L'Éditeur se réserve le droit de suspendre ou de résilier un compte
            en cas de non-respect des présentes CGU, d'usage frauduleux, ou de
            comportement portant atteinte au bon fonctionnement du Service.
          </p>

          <h2 className="legal-h2">3. Tarifs</h2>
          <p className="legal-p">
            Le Service est gratuit. Il permet d'estimer la performance énergétique
            d'un logement et de consulter les résultats de base (étiquettes
            énergétique et GES, déperditions, recommandations générales).
          </p>

          <h2 className="legal-h2">4. Obligations de l'utilisateur</h2>
          <ul className="legal-ul">
            <li>Fournir des informations exactes et sincères lors de la saisie des caractéristiques du logement.</li>
            <li>Ne pas créer de compte au nom d'une tierce personne sans son consentement.</li>
            <li>Ne pas tenter d'accéder aux données d'autres utilisateurs ni de compromettre la sécurité du Service.</li>
            <li>Ne pas utiliser le Service à des fins illégales ou commerciales sans autorisation.</li>
            <li>Respecter les droits de propriété intellectuelle de l'Éditeur.</li>
          </ul>

          <h2 className="legal-h2">5. Données personnelles et RGPD</h2>
          <p className="legal-p">
            Le traitement de vos données personnelles est décrit dans notre{" "}
            <Link to="/confidentialite" className="legal-link">
              politique de confidentialité
            </Link>
            . Vous disposez d'un droit d'accès, de rectification, de portabilité
            et de suppression de vos données. Vous pouvez exercer ces droits
            depuis votre{" "}
            <Link to="/account" className="legal-link">page compte</Link> ou par
            e-mail à l'adresse indiquée dans les{" "}
            <Link to="/mentions-legales" className="legal-link">mentions légales</Link>.
          </p>

          <h2 className="legal-h2">6. Cookies</h2>
          <p className="legal-p">
            Le Service utilise un cookie de session strictement nécessaire à
            l'authentification. L'utilisation d'éventuels cookies de mesure
            d'audience est décrite dans notre{" "}
            <Link to="/cookies" className="legal-link">politique de gestion des cookies</Link>.
          </p>

          <h2 className="legal-h2">7. Propriété intellectuelle</h2>
          <p className="legal-p">
            Le Service, son code source, son design, ses textes et ses
            illustrations sont la propriété exclusive de l'Éditeur. Toute
            reproduction, représentation, modification ou diffusion, totale ou
            partielle, sans autorisation écrite préalable, est interdite.
          </p>
          <p className="legal-p">
            La méthode 3CL-DPE 2021 est un texte réglementaire public (arrêté du
            31 mars 2021). Les données de référence ADEME utilisées pour la
            vérification du moteur sont publiques (data.ademe.fr).
          </p>

          <h2 className="legal-h2">8. Limitation de responsabilité</h2>
          <p className="legal-p">
            Le Service fournit une <b>estimation pédagogique</b> basée sur les
            informations saisies par l'utilisateur. L'Éditeur ne saurait être tenu
            responsable des décisions prises sur la base de cette estimation
            (travaux, investissements, démarches administratives). L'estimation
            ne constitue pas un DPE réglementaire et n'est pas opposable.
          </p>
          <p className="legal-p">
            L'Éditeur met tout en œuvre pour assurer la disponibilité du Service
            mais ne garantit pas un accès ininterrompu. Le Service peut être
            temporairement indisponible pour maintenance ou mise à jour.
          </p>

          <h2 className="legal-h2">9. Liens partenaires et affiliation</h2>
          <p className="legal-p">
            Le Service peut afficher des liens vers des partenaires commerciaux
            (artisans, fournisseurs d'énergie, courtiers en travaux). Ces liens
            sont identifiés comme tels (mention « partenaire »). L'Éditeur peut
            percevoir une commission lorsqu'un utilisateur recourt à un partenaire
            via ces liens, sans surcoût pour l'utilisateur.
          </p>
          <p className="legal-p">
            Les liens vers les sites officiels (ADEME, annuaire des
            diagnostiqueurs) sont identifiés par la mention « officiel » et ne
            génèrent aucune rémunération.
          </p>

          <h2 className="legal-h2">10. Modification des CGU</h2>
          <p className="legal-p">
            Les présentes CGU peuvent être modifiées à tout moment. La version
            applicable est celle en ligne à la date d'utilisation du Service. Les
            utilisateurs seront informés par e-mail en cas de modification
            substantielle. La version acceptée à l'inscription est tracée dans
            votre compte (version et date d'acceptation).
          </p>

          <h2 className="legal-h2">11. Droit applicable et juridiction</h2>
          <p className="legal-p">
            Les présentes CGU sont régies par le droit français. En cas de
            litige, une solution amiable sera recherchée prioritairement. À
            défaut, les tribunaux français seront seuls compétents.
          </p>

          <h2 className="legal-h2">12. Contact</h2>
          <p className="legal-p">
            Pour toute question relative aux présentes CGU, vous pouvez contacter
            l'Éditeur aux coordonnées indiquées dans les{" "}
            <Link to="/mentions-legales" className="legal-link">mentions légales</Link>.
          </p>
        </div>
      </div>
    </Layout>
  );
}
